import { useState, useMemo, useEffect, ChangeEvent } from "react";
import { throttle } from "lodash";

type UseThrottledSearchProps = {
    initialSearch?: string;
    delay?: number;
    onSearchChange: (value?: string) => void;
};

export function useThrottledSearch({
    initialSearch = "",
    delay = 500,
    onSearchChange,
}: UseThrottledSearchProps) {
    const [searchTerm, setSearchTerm] = useState(initialSearch);

    // Memoized throttled function
    const throttledSearch = useMemo(
        () =>
            throttle((value: string) => {
                onSearchChange(value.trim() || undefined);
            }, delay),
        [onSearchChange, delay]
    );

    const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchTerm(value);
        throttledSearch(value);
    };

    const handleSearchSubmit = () => {
        throttledSearch.cancel(); // Cancel any pending throttled call
        onSearchChange(searchTerm.trim() || undefined);
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            throttledSearch.cancel();
        };
    }, [throttledSearch]);

    return {
        searchTerm,
        setSearchTerm,
        handleSearchChange,
        handleSearchSubmit,
    };
}
